// src/components/ChartComponent/ChartComponent.js - FIXED VERSION WITH PROPER DATE HANDLING
import React, { Suspense, lazy, useRef, useEffect, useContext } from 'react';
import BlurredChartPlaceholder from '../BlurredChartPlaceholder/BlurredChartPlaceholder';
import { LoadingContext } from '../ChartControls/ChartControls';
import './ChartComponent.css';

const ReactECharts = lazy(() => import('echarts-for-react'));

const ChartComponent = ({ 
  isLoading: propsIsLoading, 
  chartId, 
  data, 
  title, 
  chartType, 
  getOption, 
  showPeaks = false
}) => {
  const chartRef = useRef(null);
  // Access the global loading context
  const { isLoading: contextIsLoading } = useContext(LoadingContext);
  
  // Combine the component's loading state with the context loading state
  const isLoading = propsIsLoading || contextIsLoading;
  
  // Dark mode disabled - always use light mode
  const isDarkMode = () => false;
  
  // Function to safely resize the chart
  const safelyResizeChart = () => {
    // Check if the ref exists and has the echarts instance
    if (chartRef.current && 
        chartRef.current.getEchartsInstance && 
        typeof chartRef.current.getEchartsInstance === 'function') {
      try {
        const echartsInstance = chartRef.current.getEchartsInstance();
        if (echartsInstance && typeof echartsInstance.resize === 'function') {
          echartsInstance.resize();
        }
      } catch (err) {
        console.log('Error resizing chart:', err);
      }
    }
  };

  // Handle resize events
  useEffect(() => {
    const handleResize = () => {
      safelyResizeChart();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Initial resize after a delay to ensure component is mounted
    const resizeTimer = setTimeout(() => {
      handleResize();
    }, 300);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);
  
  // Resize when data changes
  useEffect(() => {
    // Only resize if we have data and it's not loading
    if (data?.length > 0 && !isLoading) {
      const resizeTimer = setTimeout(() => {
        safelyResizeChart();
      }, 100);
      
      return () => clearTimeout(resizeTimer);
    }
  }, [data, chartType, isLoading]);

  // Determine the appropriate chart type based on data and chart ID
  const determineChartType = () => {
    // For first chart (ratings data), always use bar chart
    if (chartId === "chart1") {
      return "bar";
    }
    
    // For yes/no questions (chart2 and chart3), use pie charts
    if ((chartId === "chart2" || chartId === "chart3") && 
        data && data.some(item => item.name && ['Yes', 'No', 'yes', 'no'].includes(item.name))) {
      return "pie";
    }
    
    // For the last chart (happy vs unhappy), always use line chart
    if (chartId === "chart4" || (title && title.toLowerCase().includes('happy response vs unhappy'))) {
      return "line";
    }
    
    // Default to the provided chart type
    return chartType || "bar";
  };

  // Get dynamic color palette based on theme
  const getDynamicColorPalette = () => {
    const darkMode = isDarkMode();
    
    if (darkMode) {
      return [
        '#60A5FA', // Brighter blue
        '#34D399', // Brighter green
        '#FBBF24', // Brighter yellow
        '#F87171', // Brighter red
        '#A78BFA', // Brighter purple
        '#FB7185', // Brighter pink
        '#F59E0B', // Brighter orange
        '#10B981', // Brighter emerald
        '#3B82F6', // Brighter blue
        '#8B5CF6'  // Brighter violet
      ];
    } else {
      return [
        '#5470C6', // Blue
        '#91CC75', // Green  
        '#FAC858', // Yellow
        '#EE6666', // Red
        '#73C0DE', // Light Blue
        '#3BA272', // Dark Green
        '#FC8452', // Orange
        '#9A60B4', // Purple
        '#EA7CCC', // Pink
        '#6B9BD2'  // Steel Blue
      ];
    }
  };

  // Function to enhance chart options with dark mode support
  const getEnhancedOptions = (title, data, type, showPeaks) => {
    // Detect if we're in dark mode
    const darkMode = isDarkMode();
    
    // Define colors based on theme
    const textColor = darkMode ? '#f1f5f9' : '#374151';
    const gridLineColor = darkMode ? '#475569' : '#e5e7eb';
    const axisLineColor = darkMode ? '#64748b' : '#d1d5db';
    const backgroundColor = darkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)';
    
    // Get theme-appropriate color palette
    const colorPalette = getDynamicColorPalette();

    // Validate data first
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.warn('Invalid or empty data provided to chart');
      return {
        color: colorPalette,
        backgroundColor: 'transparent',
        tooltip: { 
          trigger: 'item',
          backgroundColor: backgroundColor,
          borderColor: darkMode ? '#475569' : '#e5e7eb',
          textStyle: { color: textColor }
        },
        xAxis: { 
          type: 'category', 
          data: ['No Data'],
          axisLabel: { color: textColor },
          axisLine: { lineStyle: { color: axisLineColor } }
        },
        yAxis: { 
          type: 'value',
          axisLabel: { color: textColor },
          axisLine: { lineStyle: { color: axisLineColor } }
        },
        series: [{
          data: [{ value: 0, itemStyle: { color: colorPalette[0] } }],
          type: 'bar',
          name: 'No Data'
        }],
        grid: { 
          top: '10%',
          borderColor: gridLineColor,
          backgroundColor: 'transparent'
        }
      };
    }

    // Determine the appropriate chart type based on data and chart ID
    const effectiveChartType = determineChartType();
      
    let options;
    try {
      if (getOption && typeof getOption === 'function') {
        options = getOption(title || '', data, effectiveChartType, showPeaks);
        
        // Validate the returned options
        if (!options || typeof options !== 'object') {
          throw new Error('getOption returned invalid options');
        }
        
        // Ensure color palette is preserved/added
        if (!options.color) {
          options.color = colorPalette;
        }
        
        // Validate series data and add coloring
        if (options.series && Array.isArray(options.series)) {
          options.series = options.series.map((series, seriesIndex) => {
            // Ensure each series has valid data
            if (!series.data || !Array.isArray(series.data) || series.data.length === 0) {
              console.warn('Series with empty data detected, providing fallback');
              return {
                ...series,
                data: [{ value: 0, itemStyle: { color: colorPalette[seriesIndex % colorPalette.length] } }],
                name: series.name || 'No Data'
              };
            }
            
            // Validate data structure based on chart type and add colors
            if (effectiveChartType === 'pie') {
              // Pie chart data should be objects with name and value
              series.data = series.data.map((item, index) => {
                let processedItem;
                if (typeof item === 'object' && item !== null && 'value' in item) {
                  processedItem = {
                    name: item.name || 'Unknown',
                    value: typeof item.value === 'number' ? item.value : 0,
                    itemStyle: item.itemStyle || { color: colorPalette[index % colorPalette.length] }
                  };
                } else {
                  // Convert simple values to pie chart format
                  processedItem = {
                    name: String(item),
                    value: 1,
                    itemStyle: { color: colorPalette[index % colorPalette.length] }
                  };
                }
                return processedItem;
              });
            } else {
              // For line/bar charts, ensure numeric values and add colors
              series.data = series.data.map((item, index) => {
                let processedItem;
                if (Array.isArray(item)) {
                  // Handle [x, y] format
                  processedItem = [item[0], typeof item[1] === 'number' ? item[1] : 0];
                } else if (typeof item === 'number') {
                  processedItem = item;
                } else if (typeof item === 'object' && item !== null && 'value' in item) {
                  processedItem = {
                    value: typeof item.value === 'number' ? item.value : 0,
                    itemStyle: item.itemStyle || { color: colorPalette[index % colorPalette.length] },
                    name: item.name
                  };
                } else {
                  processedItem = {
                    value: 0,
                    itemStyle: { color: colorPalette[index % colorPalette.length] }
                  };
                }
                return processedItem;
              });
              
              // Add series-level color if not present
              if (!series.itemStyle && effectiveChartType === 'bar') {
                series.itemStyle = {
                  color: colorPalette[seriesIndex % colorPalette.length]
                };
              }
              
              // For line charts, add line color
              if (effectiveChartType === 'line') {
                series.lineStyle = series.lineStyle || {
                  color: colorPalette[seriesIndex % colorPalette.length],
                  width: 3
                };
                series.itemStyle = series.itemStyle || {
                  color: colorPalette[seriesIndex % colorPalette.length]
                };
              }
            }
            
            return series;
          });
        } else {
          // No series provided, create a default one with colors
          options.series = [{
            data: [{ value: 0, itemStyle: { color: colorPalette[0] } }],
            type: effectiveChartType,
            name: 'Data'
          }];
        }
        
        // Remove the title from the options since ChartSection handles titles
        if (options.title) {
          delete options.title;
          // Adjust grid top margin since no title
          if (options.grid) {
            options.grid.top = options.grid.top || '10%';
          }
        }
        
      } else {
        // Fallback options with valid data structure and colors
        const fallbackData = data.map((item, index) => ({
          value: typeof item === 'object' && item.value ? item.value : (typeof item === 'number' ? item : index + 1),
          name: typeof item === 'object' && item.name ? item.name : `Item ${index + 1}`,
          itemStyle: { color: colorPalette[index % colorPalette.length] }
        }));
        
        const fallbackSeries = [{
          data: fallbackData,
          type: effectiveChartType,
          name: 'Data'
        }];
        
        options = {
          color: colorPalette,
          backgroundColor: 'transparent',
          tooltip: {
            trigger: effectiveChartType === 'pie' ? 'item' : 'axis',
            backgroundColor: backgroundColor,
            borderColor: darkMode ? '#475569' : '#e5e7eb',
            textStyle: { color: textColor }
          },
          xAxis: effectiveChartType !== 'pie' ? {
            type: 'category',
            data: data.map((item, index) => 
              typeof item === 'object' && item.name ? item.name : `Item ${index + 1}`
            ),
            axisLabel: { color: textColor },
            axisLine: { lineStyle: { color: axisLineColor } }
          } : undefined,
          yAxis: effectiveChartType !== 'pie' ? {
            type: 'value',
            axisLabel: { color: textColor },
            axisLine: { lineStyle: { color: axisLineColor } }
          } : undefined,
          series: fallbackSeries,
          grid: {
            top: '10%',
            borderColor: gridLineColor,
            backgroundColor: 'transparent'
          }
        };
      }

      // Apply comprehensive dark mode styling to the final options
      options = {
        ...options,
        color: colorPalette,
        backgroundColor: 'transparent',
        
        // Style the tooltip
        tooltip: {
          ...options.tooltip,
          backgroundColor: backgroundColor,
          borderColor: darkMode ? '#475569' : '#e5e7eb',
          textStyle: {
            color: textColor,
            fontSize: 12
          }
        },
        
        // Style the legend
        legend: {
          ...options.legend,
          textStyle: {
            color: textColor,
            fontSize: 12
          }
        },
        
        // Style the X-axis
        xAxis: options.xAxis ? {
          ...options.xAxis,
          axisLine: {
            lineStyle: {
              color: axisLineColor
            }
          },
          axisTick: {
            lineStyle: {
              color: axisLineColor
            }
          },
          axisLabel: {
            color: textColor,
            fontSize: 11,
            rotate: 0,
            interval: 'auto',
            formatter: function(value) {
              // For date values, no need for complex formatting since 
              // ChartOptions now handles this properly
              if (typeof value === 'string' && value.length > 10) {
                return value.substring(0, 8) + '...';
              }
              return value;
            }
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: gridLineColor,
              type: 'dashed',
              opacity: 0.3
            }
          }
        } : undefined,
        
        // Style the Y-axis
        yAxis: options.yAxis ? {
          ...options.yAxis,
          axisLine: {
            lineStyle: {
              color: axisLineColor
            }
          },
          axisTick: {
            lineStyle: {
              color: axisLineColor
            }
          },
          axisLabel: {
            color: textColor,
            fontSize: 11
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: gridLineColor,
              type: 'dashed',
              opacity: 0.3
            }
          }
        } : undefined,
        
        // Style the grid
        grid: {
          ...options.grid,
          borderColor: gridLineColor,
          backgroundColor: 'transparent'
        }
      };
      
    } catch (error) {
      console.error('Error getting chart options:', error);
      // Return safe fallback options with valid data and colors
      const safeData = data && data.length > 0 ? 
        data.map((item, index) => ({
          value: typeof item === 'number' ? item : 1,
          itemStyle: { color: colorPalette[index % colorPalette.length] }
        })) : 
        [{ value: 0, itemStyle: { color: colorPalette[0] } }];
        
      options = {
        color: colorPalette,
        backgroundColor: 'transparent',
        tooltip: { 
          trigger: 'item',
          backgroundColor: backgroundColor,
          borderColor: darkMode ? '#475569' : '#e5e7eb',
          textStyle: { color: textColor }
        },
        xAxis: { 
          type: 'category', 
          data: ['Data Points'],
          axisLabel: { color: textColor },
          axisLine: { lineStyle: { color: axisLineColor } }
        },
        yAxis: { 
          type: 'value',
          axisLabel: { color: textColor },
          axisLine: { lineStyle: { color: axisLineColor } }
        },
        series: [{
          data: safeData,
          type: 'bar',
          name: 'Fallback Data'
        }],
        grid: { 
          top: '10%',
          borderColor: gridLineColor,
          backgroundColor: 'transparent'
        }
      };
    }
    
    // Final validation and color preservation
    if (!options || typeof options !== 'object') {
      options = {
        color: colorPalette,
        backgroundColor: 'transparent',
        xAxis: { 
          type: 'category', 
          data: ['Error'],
          axisLabel: { color: textColor }
        },
        yAxis: { 
          type: 'value',
          axisLabel: { color: textColor }
        },
        series: [{ 
          data: [{ value: 0, itemStyle: { color: colorPalette[0] } }], 
          type: 'bar' 
        }]
      };
    }
    
    // Ensure color palette is always present
    if (!options.color) {
      options.color = colorPalette;
    }
    
    // Ensure transparent background
    options.backgroundColor = 'transparent';
    
    return options;
  };

  // Get the effective chart type for the placeholder
  const effectiveChartType = determineChartType();

  return (
    <div className="chart-container">
      <Suspense fallback={<BlurredChartPlaceholder type={effectiveChartType} />}>
        {isLoading ? (
          <BlurredChartPlaceholder type={effectiveChartType} />
        ) : data && data.length > 0 ? (
          <div className="chart-content">
            <ReactECharts 
              ref={chartRef}
              option={getEnhancedOptions(title || `Chart ${chartId}`, data, chartType, showPeaks)} 
              lazyUpdate={true}
              notMerge={true}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
            />
          </div>
        ) : (
          <BlurredChartPlaceholder type={effectiveChartType} message="No data available"/>
        )}
      </Suspense>
    </div>
  );
};

export default ChartComponent;